import { Button, Card } from "react-bootstrap";
import type { Banner, BannerStatus } from "@services/banners/commands/cmdListBanners";
import { formatEtShort } from "@/shared/utils/date-utils/easternTime";
import InitialsAvatar from "./InitialsAvatar";

interface BannersTableProps {
    banners: Banner[];
    onEdit: (banner: Banner) => void;
    onTurnOff: (banner: Banner) => void;
    onDelete: (banner: Banner) => void;
}

export function BannerStatusBadge({ status }: { status: BannerStatus }) {
    return <span className={`badge banner-status banner-status--${status.toLowerCase()}`}>{status}</span>;
}

const muted = (text: string) => <span className="text-muted">{text}</span>;

function endCell(b: Banner) {
    if (b.ends_at) return formatEtShort(b.ends_at);
    return muted(b.status === "Draft" ? "—" : "Until turned off");
}

/**
 * Every banner, in the server's order (drafts first, then newest created). Expired rows are
 * read-only. Live banners can only be turned off, never deleted, so the record of what clients
 * saw survives.
 */
export default function BannersTable({ banners, onEdit, onTurnOff, onDelete }: BannersTableProps) {
    return (
        <Card className="banners-page__card">
            <Card.Body>
                <h2 className="banners-page__card-title">
                    All Banners <span className="text-muted fw-normal">({banners.length})</span>
                </h2>
                <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0 banners-table">
                        <thead>
                            <tr>
                                <th>Message</th>
                                <th>Link</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Status</th>
                                <th>Created By</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {banners.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">No banners yet.</td>
                                </tr>
                            )}
                            {banners.map((b) => (
                                <tr key={b.uuid}>
                                    <td className="banners-table__message" title={b.message}>{b.message}</td>
                                    <td>
                                        {b.link_text ? (
                                            <a className="banners-table__link" href={b.link_url} target="_blank" rel="noopener noreferrer" title={b.link_url}>{b.link_text}</a>
                                        ) : (
                                            <span className="text-muted fst-italic">—</span>
                                        )}
                                    </td>
                                    <td className="banners-table__date">{b.starts_at ? formatEtShort(b.starts_at) : muted("—")}</td>
                                    <td className="banners-table__date">{endCell(b)}</td>
                                    <td><BannerStatusBadge status={b.status} /></td>
                                    <td className="text-nowrap">
                                        <span className="d-inline-flex align-items-center gap-2">
                                            <InitialsAvatar name={b.created_by} size={20} />
                                            {b.created_by}
                                        </span>
                                    </td>
                                    <td className="text-nowrap text-end">
                                        {b.status === "Expired" ? (
                                            <span className="fst-italic text-muted">Read only</span>
                                        ) : (
                                            <div className="d-flex gap-2 justify-content-end">
                                                <Button variant="neutral" size="sm" onClick={() => onEdit(b)}>Edit</Button>
                                                {b.status === "Live" ? (
                                                    <Button variant="danger" size="sm" onClick={() => onTurnOff(b)}>Turn Off</Button>
                                                ) : (
                                                    <Button variant="danger" size="sm" onClick={() => onDelete(b)}>Delete</Button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card.Body>
        </Card>
    );
}
